'use client';

import React, { useState } from 'react';
import MasjidHeader from '../components/MasjidHeader';
import MasjidFooter from '../components/MasjidFooter';
import Link from 'next/link';

const eventTypes = [
  {
    nameOfAllah: 'AL-JAMI\'',
    arabicName: 'الْجَامِع',
    meaning: 'The Gatherer',
    ayat: 'يَا أَيُّهَا الَّذِينَ آمَنُوا إِذَا نُودِيَ لِلصَّلَاةِ مِن يَوْمِ الْجُمُعَةِ فَاسْعَوْا إِلَىٰ ذِكْرِ اللَّهِ',
    ayatRef: 'Quran 62:9',
    ayatTrans: '"O you who believe, when the call to prayer is made on Friday, hasten to the remembrance of Allah."',
    icon: '📖',
    arabic: 'الجمعة',
    title: 'Jumu\'ah Khutbahs',
    category: 'Weekly',
    frequency: 'Every Friday',
    time: '1st at 1:15 PM · 2nd at 2:15 PM',
    desc: 'Two weekly Friday prayers with khutbah delivered by Imam Abdullah Madyun. The Jumu\'ah is the cornerstone of the weekly community gathering — a renewal of the covenant of brotherhood and sisterhood. All are welcome.',
    details: ['1st Jumu\'ah — 1:15 PM', '2nd Jumu\'ah — 2:15 PM', 'Khutbah by Imam Madyun', 'Every Friday', '321 E Pershing Rd'],
  },
  {
    nameOfAllah: 'AL-WADUD',
    arabicName: 'الْوَدُود',
    meaning: 'The Loving',
    ayat: 'إِنَّمَا الْمُؤْمِنُونَ إِخْوَةٌ',
    ayatRef: 'Quran 49:10',
    ayatTrans: '"The believers are but brothers."',
    icon: '🤝',
    arabic: 'أخوة',
    title: 'Bonds to Brotherhood Talks',
    category: 'Brotherhood',
    frequency: 'Ongoing Series',
    time: 'Contact for schedule',
    desc: 'Bonds to Brotherhood is a series of talks and gatherings focused on strengthening the bonds of brotherhood among Muslim men — addressing the spiritual, social, and communal dimensions of brotherhood in Islam. Each talk is grounded in Quran, Sunnah, and the lived reality of the Bronzeville community.',
    details: ['Ongoing talk series', 'Brotherhood circles', 'Quran & Sunnah grounded', 'Open to all Muslim men', 'Contact for schedule'],
  },
  {
    nameOfAllah: 'AL-ALIM',
    arabicName: 'الْعَلِيم',
    meaning: 'The All-Knowing',
    ayat: 'اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ',
    ayatRef: 'Quran 96:1',
    ayatTrans: '"Read in the name of your Lord who created."',
    icon: '📚',
    arabic: 'القرآن',
    title: 'Quran Halaqas',
    category: 'Education',
    frequency: 'Ongoing',
    time: 'After Isha prayer',
    desc: 'Ongoing Quran study circles for all levels — from beginners learning to read to advanced students studying Tafsir. The Quran Halaqa is a space of learning, reflection, and community. The first command of revelation was to read — these circles are the fulfillment of that command.',
    details: ['After Isha prayer', 'All levels welcome', 'Tajweed & Tafsir', 'Ongoing circles', 'Contact for schedule'],
  },
  {
    nameOfAllah: 'AR-RAZZAQ',
    arabicName: 'الرَّزَّاق',
    meaning: 'The Ever-Provider',
    ayat: 'وَمَا مِن دَابَّةٍ فِي الْأَرْضِ إِلَّا عَلَى اللَّهِ رِزْقُهَا',
    ayatRef: 'Quran 11:6',
    ayatTrans: '"There is no creature on earth whose provision is not guaranteed by Allah."',
    icon: '🍽️',
    arabic: 'إطعام',
    title: 'Sunday Feeding Program',
    category: 'Community',
    frequency: 'Every Sunday',
    time: 'Sunday afternoons',
    desc: 'Every Sunday, Masjid Al-Ihsan serves a community meal to all who come. No one is turned away. The Sunday Feeding Program is a living expression of the Amanah of provision in Bronzeville — bridging the food desert to the table of the Masjid.',
    details: ['Every Sunday', 'Community meal served', 'All are welcome', 'No registration required', '321 E Pershing Rd'],
  },
  {
    nameOfAllah: 'AL-KARIM',
    arabicName: 'الْكَرِيم',
    meaning: 'The Most Generous',
    ayat: 'وَأَطْعِمُوا الْقَانِعَ وَالْمُعْتَرَّ',
    ayatRef: 'Quran 22:36',
    ayatTrans: '"And feed the needy who does not ask and the one who asks."',
    icon: '🚗',
    arabic: 'إفطار متنقل',
    title: 'Iftar on Wheels',
    category: 'Ramadan',
    frequency: 'During Ramadan',
    time: 'Before Maghrib',
    desc: 'During Ramadan, Masjid Al-Ihsan\'s Iftar on Wheels program delivers hot iftar meals to those who cannot come to the Masjid — serving Bronzeville and surrounding neighborhoods with mobile iftar distribution. Al-Karim — The Most Generous — brings the table to those who cannot reach it.',
    details: ['During Ramadan only', 'Mobile iftar delivery', 'Bronzeville & surrounding areas', 'Contact Masjid for schedule'],
  },
  {
    nameOfAllah: 'AL-MUGHNI',
    arabicName: 'الْمُغْنِي',
    meaning: 'The Enricher',
    ayat: 'خُذْ مِنْ أَمْوَالِهِمْ صَدَقَةً تُطَهِّرُهُمْ وَتُزَكِّيهِم بِهَا',
    ayatRef: 'Quran 9:103',
    ayatTrans: '"Take from their wealth a charity by which you purify them and cause them increase."',
    icon: '💰',
    arabic: 'الزكاة',
    title: 'Zakaat Distribution',
    category: 'Service',
    frequency: 'Annual / Ramadan',
    time: 'Contact for schedule',
    desc: 'Annual Zakaat Al-Mal and Zakaat Al-Fitr collection and distribution. The Masjid facilitates the third pillar of Islam — purification of wealth through giving. Zakat applications available for eligible community members.',
    details: ['Annual Zakaat Al-Mal', 'Zakaat Al-Fitr (Ramadan)', 'Local distribution', 'Applications available', 'Contact for guidance'],
  },
  {
    nameOfAllah: 'AL-KARIM',
    arabicName: 'الْكَرِيم',
    meaning: 'The Most Generous',
    ayat: 'وَلِتُكَبِّرُوا اللَّهَ عَلَىٰ مَا هَدَاكُمْ وَلَعَلَّكُمْ تَشْكُرُونَ',
    ayatRef: 'Quran 2:185',
    ayatTrans: '"Glorify Allah for having guided you, and so that you may be grateful."',
    icon: '🌙',
    arabic: 'العيد',
    title: 'Eid Salah',
    category: 'Celebration',
    frequency: 'Twice yearly',
    time: 'Morning of Eid',
    desc: 'Eid Al-Fitr and Eid Al-Adha prayers for the entire community. A joyful celebration of faith, family, and community. Every family, every neighbor, every stranger is welcome at Masjid Al-Ihsan.',
    details: ['Eid Al-Fitr — End of Ramadan', 'Eid Al-Adha — Day of Sacrifice', 'Community gathering', 'All families welcome'],
  },
];

const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
  Weekly:     { bg: 'rgba(26,46,90,0.06)',  text: '#1a2e5a',  border: 'rgba(26,46,90,0.15)' },
  Brotherhood:{ bg: 'rgba(180,150,80,0.08)', text: '#7c5c10',  border: 'rgba(180,150,80,0.2)' },
  Education:  { bg: 'rgba(124,58,237,0.06)', text: '#6d28d9',  border: 'rgba(124,58,237,0.15)' },
  Community:  { bg: 'rgba(5,150,105,0.06)',  text: '#065f46',  border: 'rgba(5,150,105,0.15)' },
  Ramadan:    { bg: 'rgba(26,46,90,0.08)',   text: '#1a2e5a',  border: 'rgba(26,46,90,0.2)' },
  Service:    { bg: 'rgba(180,150,80,0.08)', text: '#7c5c10',  border: 'rgba(180,150,80,0.2)' },
  Celebration:{ bg: 'rgba(20,184,166,0.06)', text: '#0f766e',  border: 'rgba(20,184,166,0.15)' },
};

const PLAYLIST_ITEMS = [
  { type: 'youtube', label: 'Latest Khutbah', placeholder: 'Paste YouTube video URL or embed code', icon: '▶️' },
  { type: 'youtube', label: 'Khutbah Archive Playlist', placeholder: 'Paste YouTube playlist URL', icon: '📋' },
  { type: 'spotify', label: 'Latest Talk / Broadcast', placeholder: 'Paste Spotify episode URL', icon: '🎵' },
  { type: 'spotify', label: 'Talks & Broadcasts Playlist', placeholder: 'Paste Spotify playlist URL', icon: '🎙️' },
];

export default function EventsPage() {
  const [activeVideo, setActiveVideo] = useState<number | null>(null);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Amiri:wght@400;700&display=swap');
        .mih-events-page {
          background: #f5f0e8;
          color: #0f172a;
          font-family: 'DM Sans', sans-serif;
          min-height: 100vh;
        }
        .mih-events-content { padding-top: 68px; }

        /* ── Hero ── */
        .mih-page-hero {
          position: relative;
          background: #0d1f3c;
          border-bottom: 1px solid rgba(180,150,80,0.12);
          padding: 80px 40px 64px;
          overflow: hidden;
        }
        .mih-hero-blob1 {
          position: absolute;
          width: 50vw; height: 50vw;
          max-width: 600px; max-height: 600px;
          top: -20%; right: -10%;
          background: radial-gradient(circle, rgba(30,58,138,0.35) 0%, transparent 70%);
          filter: blur(80px);
          pointer-events: none;
        }
        .mih-hero-blob2 {
          position: absolute;
          width: 35vw; height: 35vw;
          max-width: 400px; max-height: 400px;
          bottom: -20%; left: -5%;
          background: radial-gradient(circle, rgba(180,150,80,0.15) 0%, transparent 70%);
          filter: blur(100px);
          pointer-events: none;
        }
        .mih-hero-grid {
          position: absolute;
          inset: 0;
          background-image: linear-gradient(rgba(180,150,80,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(180,150,80,0.04) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
        }
        .mih-page-hero-inner {
          max-width: 1320px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }
        .mih-breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 28px;
        }
        .mih-breadcrumb a {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgba(255,255,255,0.35);
          text-decoration: none;
          transition: color 0.2s;
        }
        .mih-breadcrumb a:hover { color: rgba(255,255,255,0.7); }
        .mih-breadcrumb-sep { font-size: 12px; color: rgba(255,255,255,0.2); }
        .mih-breadcrumb-current { font-family: 'DM Sans', sans-serif; font-size: 12px; color: rgba(255,255,255,0.3); }
        .mih-hero-subhaal-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: monospace;
          font-size: 8px;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: rgba(180,150,80,0.6);
          background: rgba(180,150,80,0.08);
          border: 1px solid rgba(180,150,80,0.2);
          padding: 5px 12px;
          margin-bottom: 20px;
        }
        .mih-hero-allah {
          display: flex;
          align-items: baseline;
          gap: 16px;
          margin-bottom: 12px;
        }
        .mih-hero-allah-ar {
          font-family: 'Amiri', serif;
          font-size: clamp(28px, 4vw, 48px);
          color: rgba(255,255,255,0.1);
        }
        .mih-hero-allah-en {
          font-family: monospace;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(180,150,80,0.5);
        }
        .mih-page-title {
          font-family: 'Amiri', serif;
          font-size: clamp(32px, 5vw, 60px);
          font-weight: 700;
          color: rgba(255,255,255,0.92);
          margin: 0 0 16px;
          line-height: 1.1;
        }
        .mih-page-subtitle {
          font-family: 'DM Sans', sans-serif;
          font-size: 16px;
          color: rgba(255,255,255,0.45);
          max-width: 560px;
          line-height: 1.75;
          margin: 0 0 24px;
        }
        .mih-hero-ayat {
          border-left: 2px solid rgba(180,150,80,0.3);
          padding: 10px 16px;
          max-width: 520px;
        }
        .mih-hero-ayat-ar {
          font-family: 'Amiri', serif;
          font-size: 17px;
          color: rgba(255,255,255,0.25);
          direction: rtl;
          display: block;
          margin-bottom: 5px;
        }
        .mih-hero-ayat-trans {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgba(255,255,255,0.3);
          font-style: italic;
          line-height: 1.6;
        }

        /* ── Events Grid ── */
        .mih-events-grid {
          max-width: 1320px;
          margin: 0 auto;
          padding: 60px 40px;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
          gap: 24px;
        }
        .mih-event-card {
          background: #fff;
          border: 1px solid rgba(26,46,90,0.08);
          overflow: hidden;
          transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
        }
        .mih-event-card:hover {
          border-color: rgba(26,46,90,0.2);
          box-shadow: 0 6px 24px rgba(26,46,90,0.07);
          transform: translateY(-2px);
        }
        .mih-event-card-header {
          padding: 18px 22px 14px;
          border-bottom: 1px solid rgba(26,46,90,0.06);
          background: rgba(26,46,90,0.02);
        }
        .mih-event-allah-row {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          margin-bottom: 6px;
        }
        .mih-event-allah-en {
          font-family: monospace;
          font-size: 8px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(26,46,90,0.4);
          font-weight: 700;
        }
        .mih-event-allah-ar {
          font-family: 'Amiri', serif;
          font-size: 20px;
          color: rgba(26,46,90,0.18);
          direction: rtl;
        }
        .mih-event-ayat-ar {
          font-family: 'Amiri', serif;
          font-size: 13px;
          color: rgba(26,46,90,0.22);
          direction: rtl;
          display: block;
          margin-bottom: 3px;
          line-height: 1.6;
        }
        .mih-event-ayat-trans {
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          color: rgba(15,23,42,0.35);
          font-style: italic;
          line-height: 1.5;
        }
        .mih-event-card-body {
          padding: 18px 22px;
        }
        .mih-event-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 10px;
        }
        .mih-event-icon { font-size: 26px; line-height: 1; }
        .mih-event-arabic {
          font-family: 'Amiri', serif;
          font-size: 20px;
          color: rgba(180,150,80,0.5);
          direction: rtl;
        }
        .mih-event-cat-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          flex-wrap: wrap;
        }
        .mih-event-cat-badge {
          font-family: 'DM Sans', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 3px 10px;
          border-radius: 2px;
        }
        .mih-event-freq {
          font-family: monospace;
          font-size: 9px;
          letter-spacing: 0.12em;
          color: rgba(26,46,90,0.4);
          text-transform: uppercase;
        }
        .mih-event-title {
          font-family: 'Amiri', serif;
          font-size: 22px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 6px;
        }
        .mih-event-time {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #1a2e5a;
          margin: 0 0 10px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .mih-event-desc {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: rgba(15,23,42,0.6);
          line-height: 1.7;
          margin: 0 0 14px;
        }
        .mih-event-details {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
          border-top: 1px solid rgba(26,46,90,0.06);
          padding-top: 12px;
        }
        .mih-event-details li {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgba(26,46,90,0.6);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .mih-event-details li::before {
          content: '';
          display: block;
          width: 4px; height: 4px;
          border-radius: 50%;
          background: rgba(180,150,80,0.7);
          flex-shrink: 0;
        }

        /* ── Videos & Playlists Section ── */
        .mih-media-section {
          background: #0d1f3c;
          border-top: 1px solid rgba(180,150,80,0.1);
          border-bottom: 1px solid rgba(180,150,80,0.1);
        }
        .mih-media-inner {
          max-width: 1320px;
          margin: 0 auto;
          padding: 60px 40px;
        }
        .mih-media-header {
          margin-bottom: 40px;
        }
        .mih-media-label {
          font-family: monospace;
          font-size: 9px;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: rgba(180,150,80,0.6);
          margin-bottom: 12px;
          display: block;
        }
        .mih-media-title {
          font-family: 'Amiri', serif;
          font-size: clamp(24px, 4vw, 40px);
          font-weight: 700;
          color: rgba(255,255,255,0.9);
          margin: 0 0 10px;
        }
        .mih-media-subtitle {
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          color: rgba(255,255,255,0.4);
          line-height: 1.7;
          max-width: 560px;
        }
        .mih-media-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
          margin-bottom: 32px;
        }
        .mih-media-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.07);
          padding: 24px;
          transition: border-color 0.2s, background 0.2s;
          cursor: pointer;
        }
        .mih-media-card:hover {
          border-color: rgba(180,150,80,0.3);
          background: rgba(255,255,255,0.06);
        }
        .mih-media-card-icon {
          font-size: 28px;
          margin-bottom: 12px;
          display: block;
        }
        .mih-media-card-type {
          font-family: monospace;
          font-size: 8px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(180,150,80,0.5);
          margin-bottom: 6px;
          display: block;
        }
        .mih-media-card-label {
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: rgba(255,255,255,0.8);
          margin-bottom: 8px;
        }
        .mih-media-card-placeholder {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgba(255,255,255,0.25);
          font-style: italic;
          line-height: 1.5;
        }
        .mih-media-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: rgba(180,150,80,0.12);
          border: 1px solid rgba(180,150,80,0.3);
          color: rgba(180,150,80,0.9);
          font-family: monospace;
          font-size: 9px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          text-decoration: none;
          transition: all 0.2s;
        }
        .mih-media-cta:hover {
          background: rgba(180,150,80,0.2);
          border-color: rgba(180,150,80,0.5);
        }

        @media (max-width: 768px) {
          .mih-events-grid { grid-template-columns: 1fr; padding: 32px 20px; }
          .mih-page-hero { padding: 48px 20px 40px; }
          .mih-media-inner { padding: 40px 20px; }
          .mih-media-grid { grid-template-columns: 1fr; }
        }
      `}</style>
      <div className="mih-events-page">
        <MasjidHeader />
        <div className="mih-events-content">
          {/* Hero */}
          <div className="mih-page-hero">
            <div className="mih-hero-blob1" />
            <div className="mih-hero-blob2" />
            <div className="mih-hero-grid" />
            <div className="mih-page-hero-inner">
              <div className="mih-breadcrumb">
                <Link href="/subhaal/masjid-al-ihsan">Home</Link>
                <span className="mih-breadcrumb-sep">/</span>
                <span className="mih-breadcrumb-current">Events</span>
              </div>
              <div className="mih-hero-subhaal-badge">⬡ Masjid Al-Ihsan · Haal Project Subhaal</div>
              <div className="mih-hero-allah">
                <span className="mih-hero-allah-ar">الْجَامِع</span>
                <span className="mih-hero-allah-en">Al-Jami&apos; — The Gatherer</span>
              </div>
              <h1 className="mih-page-title">Events & Programs</h1>
              <p className="mih-page-subtitle">
                Jumu&apos;ah Khutbahs, Bonds to Brotherhood talks, Quran Halaqas, Sunday Feeding, Iftar on Wheels, Zakaat Distribution, and Eid Salah — the recurring events and programs of Masjid Al-Ihsan in Bronzeville.
              </p>
              <div className="mih-hero-ayat">
                <span className="mih-hero-ayat-ar">وَتَعَاوَنُوا عَلَى الْبِرِّ وَالتَّقْوَىٰ</span>
                <span className="mih-hero-ayat-trans">&ldquo;And cooperate in righteousness and God-consciousness.&rdquo; — Quran 5:2</span>
              </div>
            </div>
          </div>

          {/* Events Grid */}
          <div className="mih-events-grid">
            {eventTypes.map((event, i) => {
              const catStyle = categoryColors[event.category] || categoryColors['Weekly'];
              return (
                <div key={i} className="mih-event-card">
                  <div className="mih-event-card-header">
                    <div className="mih-event-allah-row">
                      <span className="mih-event-allah-en">{event.nameOfAllah} — {event.meaning}</span>
                      <span className="mih-event-allah-ar">{event.arabicName}</span>
                    </div>
                    <span className="mih-event-ayat-ar">{event.ayat}</span>
                    <span className="mih-event-ayat-trans">&ldquo;{event.ayatTrans.replace(/^"|"$/g, '')}&rdquo; — {event.ayatRef}</span>
                  </div>
                  <div className="mih-event-card-body">
                    <div className="mih-event-top">
                      <span className="mih-event-icon">{event.icon}</span>
                      <span className="mih-event-arabic">{event.arabic}</span>
                    </div>
                    <div className="mih-event-cat-row">
                      <span
                        className="mih-event-cat-badge"
                        style={{ background: catStyle.bg, color: catStyle.text, border: `1px solid ${catStyle.border}` }}
                      >
                        {event.category}
                      </span>
                      <span className="mih-event-freq">{event.frequency}</span>
                    </div>
                    <h3 className="mih-event-title">{event.title}</h3>
                    <p className="mih-event-time">🕐 {event.time}</p>
                    <p className="mih-event-desc">{event.desc}</p>
                    <ul className="mih-event-details">
                      {event.details.map((d, j) => <li key={j}>{d}</li>)}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Videos & Playlists Section */}
          <div className="mih-media-section">
            <div className="mih-media-inner">
              <div className="mih-media-header">
                <span className="mih-media-label">⬡ Masjid Al-Ihsan · Media</span>
                <h2 className="mih-media-title">Videos & Playlists</h2>
                <p className="mih-media-subtitle">
                  Watch and listen to Khutbahs, Bonds to Brotherhood talks, and broadcasts from Imam Madyun and the Masjid Al-Ihsan community. Videos and playlists are updated regularly.
                </p>
              </div>
              <div className="mih-media-grid">
                {PLAYLIST_ITEMS.map((item, i) => (
                  <div key={i} className="mih-media-card" onClick={() => setActiveVideo(activeVideo === i ? null : i)}>
                    <span className="mih-media-card-icon">{item.icon}</span>
                    <span className="mih-media-card-type">{item.type === 'youtube' ? 'YouTube' : 'Spotify'}</span>
                    <p className="mih-media-card-label">{item.label}</p>
                    <p className="mih-media-card-placeholder">{item.placeholder}</p>
                  </div>
                ))}
              </div>
              <Link href="/subhaal/masjid-al-ihsan/media" className="mih-media-cta">
                ▶ View Full Media Page →
              </Link>
            </div>
          </div>

          <MasjidFooter />
        </div>
      </div>
    </>
  );
}
